'use client'

import type * as React from 'react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface ToggleProps {
  /** Current checked state */
  checked: boolean
  /** Called when the toggle is changed */
  onChange: (checked: boolean) => void
  /** Whether the toggle is disabled */
  disabled?: boolean
  /** Additional class names */
  className?: string
  /** Aria label for accessibility */
  'aria-label'?: string
}

/**
 * A simple toggle/switch component without auto-save functionality.
 * For use in forms or settings where you manage state externally.
 */
const Toggle = ({
  checked,
  onChange,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: ToggleProps) => {
  const [isPressed, setIsPressed] = useState(false)

  const handleToggle = () => {
    if (disabled) return
    onChange(!checked)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle()
    }
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      tabIndex={0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      onMouseDown={() => !disabled && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      disabled={disabled}
      className={cn(
        'group relative h-5 w-9 rounded-full p-0.5 transition-all duration-300 ease-out',
        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {/* Thumb */}
      <div
        className={cn(
          'bg-background h-4 w-4 rounded-full shadow-sm transition-all duration-300 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)]',
          checked ? 'translate-x-4' : 'translate-x-0',
          isPressed && !disabled && 'scale-90 duration-150',
        )}
      />
    </button>
  )
}

export { Toggle }
export type { ToggleProps }
